/**
 * Auth Service for Trade Loop v2.0
 * 
 * Handles all authentication operations using cookie-based auth
 * No token storage needed - cookies are handled automatically by the browser
 */

import { api } from '@/lib/api';
import { env } from '@/lib/env';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  profileImageUrl?: string;
  companyLogoUrl?: string;
  role?: string;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  phone?: string;
  phoneConfirmedAt?: string; // Timestamp when phone was verified (from auth.users.phone_confirmed_at)
  company?: string;
  companyAddress?: string;
  trade?: string;
  location?: string;
  trades?: string[];
  marketTypes?: string[];
  serviceAreas?: string[];
  personalBio?: string;
  companyBio?: string;
  subscriptionPlan?: string;
  subscriptionStatus?: string; // active, trialing, canceled, etc.
  onboardingComplete?: boolean;
  /**
   * Whether the user has answered the post-signup CRM + primary service area
   * questions in the first-login onboarding modal. Distinct from
   * `onboardingComplete` (which flips on email confirmation).
   */
  initialOnboardingCompleted?: boolean;
  trigPrimaryTradeName?: string;
  primaryTradeId?: string;
  organizationMembership?: {
    organizationId: string;
    role: 'admin' | 'member';
    status: string;
  } | null;
  showEmailToConnections?: boolean;
  showPhoneToConnections?: boolean;
  /** IANA timezone last reported by the user's device; unset until first reported. */
  timezone?: string;
}

/**
 * Set by the server when the current session is an admin impersonating this
 * user. Server-issued only — the client never sets or infers this.
 */
export interface Impersonator {
  isImpersonating: true;
  impersonatorUserId: string;
  impersonatorName: string | null;
  impersonatorEmail: string | null;
}

export interface LoginResponse {
  user: User;
  profile: any;
  role: any;
  permissions: string[];
  // Organization membership for team members
  organizationMembership?: {
    organizationId: string;
    role: 'admin' | 'member';
    status: string;
  } | null;
  // Non-null only while an admin is impersonating this user
  impersonator?: Impersonator | null;
}

export interface SignupData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  referralCode?: string;
  startTrial?: boolean;
  /** "What CRM does your company use?" — a value from config/crm-options. */
  crmProvider?: string;
  /** Free text; sent only when crmProvider === 'other'. */
  crmProviderOther?: string;
}

/**
 * Login user - cookies are set automatically by server
 */
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  return await api.post<LoginResponse>('/api/auth/login', { email, password });
};

/**
 * Sign up new user - cookies are set automatically by server
 */
export const signup = async (data: SignupData): Promise<LoginResponse> => {
  return await api.post<LoginResponse>('/api/auth/signup', data);
};

/**
 * Logout user - clears cookies on server
 */
export const logout = async (): Promise<void> => {
  await api.post('/api/auth/logout');
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = async (): Promise<LoginResponse> => {
  return await api.get<LoginResponse>('/api/auth/me');
};

/**
 * Update user profile
 */
export const updateProfile = async (data: Partial<User>): Promise<any> => {
  return await api.put('/api/auth/profile', data);
};

/**
 * Payload for the first-login onboarding wizard. Each `serviceAreas[].state`
 * is the 2-char ISO code the StateGatedServiceAreaPicker returns, and `.city`
 * is either a city name or "County Name County" for a whole-county pick.
 *
 * The profile picture is uploaded separately via POST /api/files/avatar
 * before this call (that endpoint sets `profile_image_url` itself), so this
 * payload never carries the image URL.
 */
export interface InitialOnboardingPayload {
  firstName: string;
  lastName: string;
  company?: string;
  primaryTradeId: string;
  crmProvider: string;
  crmProviderOther?: string;
  serviceAreas: Array<{ city: string; state: string }>;
}

/**
 * Submit the two-step first-login onboarding wizard's answers. On success the
 * server sets `initial_onboarding_completed = true`, so the modal never fires
 * again for this account.
 */
export const submitInitialOnboarding = async (
  data: InitialOnboardingPayload
): Promise<{ success: boolean }> => {
  return await api.post<{ success: boolean }>('/api/auth/initial-onboarding', data);
};

/**
 * Report the device's IANA timezone. The server validates it and writes only
 * when it differs from the saved value. See useDeviceTimezoneSync.
 */
export const updateTimezone = async (
  timezone: string
): Promise<{ success: boolean; data: { timezone: string; updated: boolean } }> => {
  return await api.put('/api/workflows/profile/me/timezone', { timezone });
};

/**
 * Request password reset
 */
export const resetPassword = async (email: string): Promise<void> => {
  await api.post('/api/auth/reset-password', { email });
};

/**
 * Update password
 */
export const updatePassword = async (newPassword: string): Promise<void> => {
  await api.put('/api/auth/update-password', { newPassword });
};

/**
 * Update email address
 */
export const updateEmail = async (newEmail: string): Promise<void> => {
  await api.put('/api/auth/update-email', { newEmail });
};

/**
 * OAuth sign-in intent threaded through the Google round-trip so the backend
 * account gate (BETA_GATED_OAUTH_PLAN.md §2) can tell a login from a signup. A
 * missing intent is normalised to 'login' server-side, so 'login' is the safe
 * default everywhere on the client.
 */
export type OAuthIntent = 'login' | 'signup';

/**
 * Extra fields the OAuth callback posts alongside the Supabase tokens. Only the
 * signup path supplies `phone`/`firstName`/`lastName`; the email-confirmation
 * path omits all of these and keeps its original two-argument behaviour.
 *
 * `firstName`/`lastName` are the values the user typed into the signup form
 * before choosing an OAuth provider — the backend prefers these over anything
 * the provider itself returns so the account matches what the user just saw.
 */
export interface AuthCallbackExtras {
  intent?: OAuthIntent;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  /**
   * Referrer user id captured from a `/connect/<uuid>` invite on the way to
   * OAuth. Read by AuthCallbackPage from localStorage.pendingReferrerId and
   * forwarded to the backend so the signup path creates the bidirectional
   * connection. Ignored on `intent === 'login'` server-side.
   */
  referralCode?: string | null;
}

// localStorage keys used to carry the signup answers across the OAuth
// round-trip — same survive-the-redirect pattern the app uses for
// pendingReferralInviteToken. Written before the redirect, read + cleared
// once by AuthCallbackPage on return.
const PENDING_OAUTH_PHONE = 'pendingOAuthPhone';
const PENDING_OAUTH_FIRST_NAME = 'pendingOAuthFirstName';
const PENDING_OAUTH_LAST_NAME = 'pendingOAuthLastName';

/** What the signup tab hands to the OAuth redirect and gets back at the callback. */
export interface PendingOAuthSignup {
  phone?: string;
  /** First name typed on the signup form — required before the button unlocks. */
  firstName?: string;
  /** Last name typed on the signup form — required before the button unlocks. */
  lastName?: string;
}

/** Write a value, or remove the key entirely — never leave a previous attempt's value behind. */
const setOrClear = (key: string, value?: string): void => {
  if (value) {
    localStorage.setItem(key, value);
  } else {
    localStorage.removeItem(key);
  }
};

/**
 * Stash the signup answers before the OAuth redirect.
 *
 * localStorage rather than sessionStorage on purpose, and not a new decision:
 * this is the same store pendingReferralInviteToken already uses, and it is the
 * only one that survives every round trip we ship — a web
 * redirect, and a Capacitor build where the provider may hand the flow to an
 * external browser and come back into a fresh webview context.
 */
export const stashPendingOAuthSignup = (signup: PendingOAuthSignup): void => {
  setOrClear(PENDING_OAUTH_PHONE, signup.phone);
  setOrClear(PENDING_OAUTH_FIRST_NAME, signup.firstName);
  setOrClear(PENDING_OAUTH_LAST_NAME, signup.lastName);
};

/** Read the stashed signup answers (nulls when nothing was stashed). */
export const readPendingOAuthSignup = (): {
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
} => ({
  phone: localStorage.getItem(PENDING_OAUTH_PHONE),
  firstName: localStorage.getItem(PENDING_OAUTH_FIRST_NAME),
  lastName: localStorage.getItem(PENDING_OAUTH_LAST_NAME),
});

/** Clear the stashed signup values — call once they have been consumed. */
export const clearPendingOAuthSignup = (): void => {
  localStorage.removeItem(PENDING_OAUTH_PHONE);
  localStorage.removeItem(PENDING_OAUTH_FIRST_NAME);
  localStorage.removeItem(PENDING_OAUTH_LAST_NAME);
};

/**
 * Handle auth callback with tokens from email confirmation or Google OAuth.
 * Sends tokens to backend to establish session. For beta-gated OAuth, `extras`
 * carries the intent and (signup only) the beta key/phone; the backend gate
 * (BETA_GATED_OAUTH_PLAN.md §2) may reject a login with NO_ACCOUNT.
 */
export const handleAuthCallback = async (
  accessToken: string,
  refreshToken: string,
  extras?: AuthCallbackExtras
): Promise<LoginResponse> => {
  return await api.post<LoginResponse>('/api/auth/callback', {
    access_token: accessToken,
    refresh_token: refreshToken,
    intent: extras?.intent,
    phone: extras?.phone ?? undefined,
    firstName: extras?.firstName ?? undefined,
    lastName: extras?.lastName ?? undefined,
    referralCode: extras?.referralCode ?? undefined,
  });
};

/**
 * Build the Supabase Google OAuth authorization URL (implicit flow).
 *
 * Trade Loop uses the Supabase project only for the OAuth handshake — the
 * browser is sent to Supabase's /authorize endpoint, which bounces through
 * Google and returns the user to `redirect_to` with the Supabase session
 * tokens in the URL hash (#access_token=…&refresh_token=…). AuthCallbackPage
 * already parses that hash and posts the tokens to POST /api/auth/callback,
 * which exchanges them for the app's own httpOnly session cookie (see
 * handleAuthCallback). No PKCE code exchange is required, so this works even
 * though the shared Supabase client (lib/supabase.ts) is realtime-only.
 *
 * NOTE ON THE HIGHCALL DIFFERENCE: the referenced "highcall" project generates
 * the OAuth URL on its backend (POST /google) because it runs its own OAuth
 * code exchange server-side. Trade Loop has no such endpoint; instead it reuses
 * the existing token→cookie exchange that already backs email confirmation, so
 * the frontend builds the authorize URL directly. Google's client ID/secret
 * live in Supabase (dashboard for prod, config.toml for local) — never in app
 * env or on DigitalOcean.
 *
 * @param intent whether this is a 'login' or a 'signup' — carried on the
 *   callback query so the backend beta gate can apply the right rule (§2/§5).
 * @param redirectPath in-app path to land on after the session is established
 */
export const buildGoogleOAuthUrl = (
  intent: OAuthIntent = 'login',
  redirectPath: string = '/dashboard'
): string => buildOAuthUrl('google', intent, redirectPath);

/**
 * Social providers the web flow supports. Both go through the SAME Supabase
 * `/auth/v1/authorize` endpoint and the same callback — see buildOAuthUrl.
 */
export type OAuthProvider = 'google' | 'apple';

/**
 * Build a Supabase OAuth authorization URL (implicit flow) for any provider.
 *
 * Why Apple needs no extra machinery here: Supabase is the OAuth client, so it
 * is Supabase — not this app — that holds the Services ID, mints the ES256
 * client-secret JWT from the `.p8`, and receives Apple's `response_mode=
 * form_post` callback at `<project>.supabase.co/auth/v1/callback`. By the time
 * the browser comes back to us it is an ordinary redirect carrying the session
 * in the URL hash, exactly like Google. None of Apple's cross-site-POST or
 * SameSite=Lax cookie hazards touch our callback route at all.
 */
export const buildOAuthUrl = (
  provider: OAuthProvider,
  intent: OAuthIntent = 'login',
  redirectPath: string = '/dashboard'
): string => {
  // Return to the existing auth-callback route. `provider` only tweaks the
  // callback's copy; `intent` is read back by AuthCallbackPage and posted to
  // the gate; `redirect` is where the user is sent once the session cookie is
  // set. The tokens themselves arrive in the URL hash.
  const redirectTo = `${window.location.origin}/auth/callback?provider=${provider}&intent=${intent}&redirect=${encodeURIComponent(
    redirectPath
  )}`;
  const params = new URLSearchParams({
    provider,
    redirect_to: redirectTo,
  });
  return `${env.SUPABASE_URL}/auth/v1/authorize?${params.toString()}`;
};

/**
 * Start the Google OAuth sign-in flow by redirecting the browser to Supabase.
 * This is a full-page navigation, so nothing after it runs.
 *
 * For a signup, pass the (already pre-validated) beta key, optional phone, and
 * CRM answer in `signupData`; they are stashed in localStorage so they survive
 * the redirect and are posted to the gate by AuthCallbackPage on return.
 */
export const signInWithGoogle = (
  intent: OAuthIntent = 'login',
  redirectPath: string = '/dashboard',
  signupData?: PendingOAuthSignup
): void => signInWithOAuth('google', intent, redirectPath, signupData);

/**
 * Start the Apple OAuth sign-in flow. Same contract as {@link signInWithGoogle}
 * — full-page navigation, nothing after it runs, and the signup beta key/phone/
 * CRM answer are stashed so they survive the redirect.
 */
export const signInWithApple = (
  intent: OAuthIntent = 'login',
  redirectPath: string = '/dashboard',
  signupData?: PendingOAuthSignup
): void => signInWithOAuth('apple', intent, redirectPath, signupData);

/** Shared redirect-to-Supabase implementation behind the per-provider helpers. */
export const signInWithOAuth = (
  provider: OAuthProvider,
  intent: OAuthIntent = 'login',
  redirectPath: string = '/dashboard',
  signupData?: PendingOAuthSignup
): void => {
  if (intent === 'signup' && signupData) {
    stashPendingOAuthSignup(signupData);
  }
  window.location.href = buildOAuthUrl(provider, intent, redirectPath);
};

/**
 * Body of POST /api/auth/google-native — the NATIVE counterpart of
 * handleAuthCallback. `nonce` is the RAW (unhashed) value; the SHA-256 of it
 * went to Google (GOOGLE_OAUTH_SETUP.md §8.6).
 */
export interface NativeGoogleSignInData {
  idToken: string;
  nonce?: string;
  intent?: OAuthIntent;
  phone?: string | null;
  /** User-typed first name from the signup form, forwarded to the backend. */
  firstName?: string | null;
  /** User-typed last name from the signup form, forwarded to the backend. */
  lastName?: string | null;
  /** Referrer user id from a /connect/<uuid> invite. See AuthCallbackExtras. */
  referralCode?: string | null;
}

/**
 * Exchange a native Google id_token for an app session
 * (GOOGLE_OAUTH_NATIVE_MOBILE_PLAN.md §10). The response is byte-identical to
 * /api/auth/callback and the backend sets the same opaque `session_id` cookie —
 * card A's CapacitorHttp transport carries it, so nothing native-specific is
 * needed on the request path. The same beta gate applies, so this can reject
 * with NO_ACCOUNT (401).
 */
export const signInWithGoogleNative = async (
  data: NativeGoogleSignInData
): Promise<LoginResponse> => {
  return await api.post<LoginResponse>('/api/auth/google-native', {
    id_token: data.idToken,
    nonce: data.nonce,
    intent: data.intent,
    phone: data.phone ?? undefined,
    firstName: data.firstName ?? undefined,
    lastName: data.lastName ?? undefined,
    referralCode: data.referralCode ?? undefined,
  });
};

/**
 * Body of POST /api/auth/apple-native. Same as the Google shape plus the
 * one-shot name.
 *
 * `givenName`/`familyName` are present ONLY on a user's very first
 * authorization of this app. Apple never sends the name again — not in the
 * id_token, not on any later sign-in — so if it is not persisted now it is lost
 * for good. Every subsequent call legitimately omits them.
 */
export interface NativeAppleSignInData {
  idToken: string;
  nonce?: string;
  /**
   * Apple's one-shot name from the OS sheet — only present on the very first
   * authorization. The user-typed `firstName`/`lastName` fields below take
   * precedence on the backend, this is the fallback.
   */
  givenName?: string | null;
  familyName?: string | null;
  intent?: OAuthIntent;
  phone?: string | null;
  /** User-typed first name from the signup form, forwarded to the backend. */
  firstName?: string | null;
  /** User-typed last name from the signup form, forwarded to the backend. */
  lastName?: string | null;
  /** Referrer user id from a /connect/<uuid> invite. See AuthCallbackExtras. */
  referralCode?: string | null;
}

/**
 * Exchange a native Apple id_token for an app session. The response is
 * byte-identical to /api/auth/callback and the backend sets the same opaque
 * `session_id` cookie. The same beta gate applies, so this can reject with
 * NO_ACCOUNT (401).
 */
export const signInWithAppleNative = async (
  data: NativeAppleSignInData
): Promise<LoginResponse> => {
  return await api.post<LoginResponse>('/api/auth/apple-native', {
    id_token: data.idToken,
    nonce: data.nonce,
    givenName: data.givenName ?? undefined,
    familyName: data.familyName ?? undefined,
    intent: data.intent,
    phone: data.phone ?? undefined,
    firstName: data.firstName ?? undefined,
    lastName: data.lastName ?? undefined,
    referralCode: data.referralCode ?? undefined,
  });
};


/**
 * Verify email OTP code after signup
 */
export const verifyOtp = async (email: string, token: string): Promise<LoginResponse> => {
  return await api.post<LoginResponse>('/api/auth/verify-otp', { email, token });
};

/**
 * Resend verification email with new OTP code
 */
export const resendVerification = async (email: string): Promise<void> => {
  await api.post('/api/auth/resend-verification', { email });
};


/**
 * Start impersonating a user (Super Admin only).
 *
 * The server swaps the httpOnly session cookie to a fresh session for the
 * target user. Callers should hard-reload afterwards rather than patching
 * client state — see ImpersonationBanner for why.
 */
export const impersonateUser = async (
  userId: string
): Promise<{ success: boolean; impersonatedUserId: string; targetName: string }> => {
  return await api.post(`/api/auth/impersonate/${userId}`, {});
};

/**
 * Return to the admin account. Authorized server-side by the impersonator
 * stamp on the session row, so this works even though the current session
 * authenticates as a non-admin user.
 */
export const stopImpersonating = async (): Promise<{ success: boolean }> => {
  return await api.post('/api/auth/impersonation/stop', {});
};
